/* ============================================================
 * AYU /angebot – Sales Page für Closing Call
 * Design: AYU Sacred Modern (Gold + Olive)
 * Struktur: 1:1 Adaption der MENTO /angebot Seite
 * ============================================================ */
import { useState, useEffect, useRef } from "react";
import { Link } from "wouter";
import { IMAGES } from "@/lib/constants";
import {
  Leaf, Heart, Shield, Clock, Users, Lock,
  MessageCircle, Brain, Infinity, ChevronDown,
  Check, X, Minus, Sparkles, ArrowRight,
  FlaskConical, Apple, Dna, Activity
} from "lucide-react";

/* ── Intersection Observer Hook ── */
function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, visible };
}

/* ── Accordion Component ── */
function Accordion({ items }: { items: { q: string; a: string }[] }) {
  const [open, setOpen] = useState<number | null>(null);
  return (
    <div className="space-y-3">
      {items.map((item, i) => (
        <div
          key={i}
          className="border rounded-2xl overflow-hidden transition-all duration-300"
          style={{ borderColor: "oklch(0.88 0.02 80)" }}
        >
          <button
            onClick={() => setOpen(open === i ? null : i)}
            className="w-full flex items-center justify-between p-5 text-left"
            style={{ fontFamily: "var(--font-body)" }}
          >
            <span className="font-semibold text-base pr-4" style={{ color: "oklch(0.25 0.03 50)" }}>
              {item.q}
            </span>
            <ChevronDown
              className={`w-5 h-5 shrink-0 transition-transform duration-300 ${open === i ? "rotate-180" : ""}`}
              style={{ color: "oklch(0.52 0.09 128)" }}
            />
          </button>
          <div
            className={`overflow-hidden transition-all duration-300 ${open === i ? "max-h-96 pb-5 px-5" : "max-h-0"}`}
          >
            <p className="text-sm leading-relaxed" style={{ color: "oklch(0.45 0.03 50)", fontFamily: "var(--font-body)" }}>
              {item.a}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── Data ── */
const FEATURES = [
  {
    icon: MessageCircle,
    title: "Umfassende Anamnese",
    desc: "Herkunft, Lebensstil, Ernährung, mentales Befinden, familiäre Muster — alles fließt in deinen persönlichen AYU ein.",
  },
  {
    icon: Heart,
    title: "Ganzheitliche Analyse",
    desc: "AYU reflektiert, was du teilst — ohne zu bewerten. Du erhältst Klarheit über Körper, Geist und Lebensstil.",
  },
  {
    icon: Brain,
    title: "Wissenschaftlich fundiert",
    desc: "5 Säulen: Ernährung, Bewegung, Mentale Balance, Tagesrhythmus und Prävention — basierend auf Ayurveda und moderner Forschung.",
  },
  {
    icon: Infinity,
    title: "Unbegrenzt verfügbar",
    desc: "Einmal eingerichtet, immer da. Dein AYU-Plan passt sich deinem Leben an — jederzeit abrufbar.",
  },
  {
    icon: Users,
    title: "Persönliche Betreuer",
    desc: "Neben deinem AYU-Plan hast du Zugang zu echten Beratern — ausgebildeten Gesundheitsbegleitern, die dich unterstützen.",
  },
  {
    icon: Lock,
    title: "Deine Daten gehören dir",
    desc: "Alles bleibt bei dir. Keine Weitergabe. Keine Analyse für Werbezwecke. Dein geschützter Raum.",
  },
];

const STEPS = [
  {
    num: "01",
    icon: MessageCircle,
    title: "Fragebogen ausfüllen",
    desc: "In ca. 15 Minuten beantwortest du Fragen zu deiner Herkunft, deinem Lebensstil, deiner Ernährung und deinem mentalen Befinden.",
    time: "15 Min.",
  },
  {
    num: "02",
    icon: Brain,
    title: "Dein Phänotyp wird bestimmt",
    desc: "Auf Basis deiner Antworten wird dein persönlicher Phänotyp bestimmt — einer von 12 menschlichen Grundtypen, basierend auf Genetik, Kultur und Umwelt.",
    time: "24 Stunden",
  },
  {
    num: "03",
    icon: Infinity,
    title: "Unbegrenzt nutzen",
    desc: "Dein AYU-Plan steht dir ab sofort zur Verfügung — jederzeit, so oft du willst. Das erste Jahr ist inklusive, danach nur eine kleine App-Gebühr.",
    time: "Für immer",
  },
];

const ACCORDION_FEATURES = [
  {
    icon: Leaf,
    title: "Personalisiert — nicht generisch",
    desc: "AYU ist kein allgemeiner Gesundheitsratgeber. Dein AYU wird auf Basis deiner umfassenden Anamnese konfiguriert. Er kennt deine Herkunft, deinen Lebensstil, deine Muster. Jede Empfehlung ist auf dich zugeschnitten.",
  },
  {
    icon: Infinity,
    title: "Einmalig zahlen — unbegrenzt nutzen",
    desc: "Keine Abos, keine versteckten Kosten. Du investierst einmal und hast unbegrenzten Zugang zu deinem personalisierten AYU-Plan.",
  },
  {
    icon: Brain,
    title: "Wissenschaftlich fundiert — 5 Säulen der Gesundheit",
    desc: "Ernährung, Bewegung, Mentale Balance, Tagesrhythmus und Prävention — basierend auf 5.000 Jahren Ayurveda und moderner Wissenschaft.",
  },
  {
    icon: Clock,
    title: "Verfügbar, wenn du ihn brauchst — 24/7",
    desc: "Dein AYU-Plan ist immer da. Morgens, abends, im Urlaub — wann immer du Orientierung brauchst.",
  },
  {
    icon: Lock,
    title: "Deine Daten gehören dir",
    desc: "Keine Weitergabe, keine Werbung. Deine Gesundheitsdaten bleiben in deinem geschützten Raum.",
  },
  {
    icon: Users,
    title: "Persönliche Betreuung inklusive",
    desc: "Echte Menschen begleiten dich. Unsere ausgebildeten Gesundheitsberater stehen dir zur Seite — nicht nur ein Algorithmus.",
  },
];

const COMPARISON = [
  { label: "Personalisiert", ayu: true, therapy: true, apps: false },
  { label: "Sofort verfügbar", ayu: true, therapy: false, apps: true },
  { label: "24/7 erreichbar", ayu: true, therapy: false, apps: true },
  { label: "Kennt deine Geschichte", ayu: true, therapy: true, apps: false },
  { label: "Keine Warteliste", ayu: true, therapy: false, apps: true },
  { label: "Ganzheitlich fundiert", ayu: true, therapy: true, apps: false },
  { label: "Einmalige Kosten", ayu: true, therapy: false, apps: false },
  { label: "Körper-Geist-Analyse", ayu: true, therapy: false, apps: false },
];

const MIKROBIOM_ITEMS = [
  { icon: Activity, title: "Serotonin-Produktion", desc: "Ist dein „Glückshormon“ in Balance? Mangel führt zu Niedergeschlagenheit und Angst." },
  { icon: Sparkles, title: "Dopamin-Zusammenhänge", desc: "Dein Antriebshormon — bei Mangel fühlst du dich antriebslos und unmotiviert." },
  { icon: FlaskConical, title: "Nährstoffmängel", desc: "Vitamin D, B12, Magnesium, Omega-3 — Defizite, die deine Gesundheit direkt beeinflussen." },
  { icon: Apple, title: "Ernährungsempfehlungen", desc: "Konkrete Hinweise, welche Lebensmittel dein Mikrobiom und damit dein Wohlbefinden verbessern." },
];

const FAQ = [
  { q: "Ersetzt AYU eine ärztliche Behandlung?", a: "Nein. AYU ist ausschließlich präventiv und bildend. Es diagnostiziert nicht, behandelt nicht und ersetzt keine ärztliche Beratung. Es hilft dir, Muster, Prioritäten und Gewohnheiten zu verstehen, die dein langfristiges Wohlbefinden unterstützen." },
  { q: "Wie lange dauert es, bis mein AYU bereit ist?", a: "Nach dem Ausfüllen des Fragebogens wird dein persönlicher AYU-Plan innerhalb von 24 Stunden erstellt. Danach kannst du ihn sofort und unbegrenzt nutzen." },
  { q: "Was passiert mit meinen Daten aus dem Fragebogen?", a: "Deine Daten werden ausschließlich für die Erstellung deines persönlichen AYU-Plans verwendet. Keine Weitergabe an Dritte, keine Nutzung für Werbezwecke. Alles bleibt in deinem geschützten Bereich." },
  { q: "Was ist der Unterschied zwischen Solo und DNA+?", a: "AYU Solo bietet dir einen vollständigen, personalisierten Gesundheitsplan basierend auf deiner Anamnese. AYU DNA+ ergänzt dies um eine DNA-Analyse und Mikrobiom-Test, sodass dein Plan auch deine körperlichen Daten berücksichtigt — für eine noch tiefere, ganzheitlichere Beratung." },
  { q: "Gibt es laufende Kosten?", a: "Das erste Jahr App-Nutzung ist bei beiden Paketen inklusive. Danach fallen nur 5 €/Monat oder 40 €/Jahr an — damit dein AYU immer für dich da bleibt." },
];

/* ── Main Component ── */
export default function Angebot() {
  const heroRef = useInView(0.1);
  const featuresRef = useInView();
  const stepsRef = useInView();
  const accordionRef = useInView();
  const comparisonRef = useInView();
  const mikrobiomRef = useInView();
  const decisionRef = useInView();
  const faqRef = useInView();
  const pricingRef = useInView();
  const ctaRef = useInView(0.1);

  const [activeAccordion, setActiveAccordion] = useState(0);

  return (
    <div className="min-h-screen" style={{ background: "oklch(0.97 0.01 80)" }}>

      {/* ═══════════════════════════════════════════════════════════
       *  1. HERO — Dark olive background
       * ═══════════════════════════════════════════════════════════ */}
      <section
        ref={heroRef.ref}
        className="relative overflow-hidden py-24 lg:py-36"
        style={{ background: "linear-gradient(135deg, oklch(0.18 0.03 128), oklch(0.22 0.04 100))" }}
      >
        {/* Mandala overlay */}
        <div className="absolute inset-0 mandala-overlay" />

        {/* Top nav */}
        <div className="absolute top-0 left-0 right-0 p-6 flex items-center justify-between z-20">
          <Link href="/">
            <img src={IMAGES.logo} alt="AYU" className="h-10" />
          </Link>
          <Link
            href="/termin"
            className="text-sm font-medium px-5 py-2.5 rounded-full transition-all duration-300"
            style={{
              color: "oklch(0.97 0.01 80)",
              border: "1px solid oklch(0.78 0.11 85 / 0.4)",
              fontFamily: "var(--font-body)",
            }}
          >
            Persönliche Beratung
          </Link>
        </div>

        <div className="container relative z-10 text-center">
          <span
            className="text-sm font-semibold tracking-[0.25em] uppercase"
            style={{ color: "oklch(0.78 0.11 85)", fontFamily: "var(--font-body)" }}
          >
            Dein nächster Schritt
          </span>
          <h1
            className={`text-4xl sm:text-5xl lg:text-6xl font-bold mt-6 leading-tight transition-all duration-1000 ${
              heroRef.visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
            style={{ color: "oklch(0.97 0.01 80)" }}
          >
            Du hast den Mut,<br />
            <em className="not-italic" style={{ color: "oklch(0.78 0.11 85)" }}>hinzuschauen.</em>
          </h1>
          <p
            className={`text-lg mt-6 max-w-2xl mx-auto leading-relaxed transition-all duration-1000 delay-200 ${
              heroRef.visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
            style={{ color: "oklch(0.85 0.02 80)", fontFamily: "var(--font-body)" }}
          >
            Das, was du gerade geteilt hast, zeigt Stärke. Jetzt geht es darum, den richtigen Begleiter für deinen Weg zu finden.
          </p>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
       *  2. WAS DU BEKOMMST — Feature Cards
       * ═══════════════════════════════════════════════════════════ */}
      <section ref={featuresRef.ref} className="py-24 lg:py-32" style={{ background: "oklch(0.97 0.01 80)" }}>
        <div className="container">
          <div className="text-center mb-16">
            <span className="text-sm font-semibold tracking-[0.2em] uppercase" style={{ color: "oklch(0.52 0.09 128)", fontFamily: "var(--font-body)" }}>
              Was du bekommst
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mt-4" style={{ color: "oklch(0.2 0.02 80)" }}>
              Dein persönlicher <em className="not-italic" style={{ color: "oklch(0.52 0.09 128)" }}>Gesundheitsplan</em>
            </h2>
            <p className="text-lg mt-4 max-w-2xl mx-auto" style={{ color: "oklch(0.45 0.03 50)", fontFamily: "var(--font-body)" }}>
              AYU ist kein allgemeiner Ratgeber. Er wird auf Basis deiner Lebensgeschichte konfiguriert &mdash; und kennt dich wirklich.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <div
                  key={i}
                  className={`p-7 rounded-2xl border transition-all duration-700 hover:shadow-lg ${
                    featuresRef.visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
                  }`}
                  style={{
                    transitionDelay: `${i * 100}ms`,
                    borderColor: "oklch(0.9 0.01 80)",
                    background: "white",
                  }}
                >
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-5"
                    style={{ background: "oklch(0.52 0.09 128 / 0.08)" }}
                  >
                    <Icon className="w-6 h-6" style={{ color: "oklch(0.52 0.09 128)" }} />
                  </div>
                  <h3 className="text-lg font-bold mb-2" style={{ color: "oklch(0.2 0.02 80)" }}>{f.title}</h3>
                  <p className="text-sm leading-relaxed" style={{ color: "oklch(0.45 0.03 50)", fontFamily: "var(--font-body)" }}>{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
       *  3. SO FUNKTIONIERT ES — 3 Steps
       * ═══════════════════════════════════════════════════════════ */}
      <section ref={stepsRef.ref} className="py-24 lg:py-32" style={{ background: "oklch(0.95 0.008 80)" }}>
        <div className="container">
          <div className="text-center mb-16">
            <span className="text-sm font-semibold tracking-[0.2em] uppercase" style={{ color: "oklch(0.52 0.09 128)", fontFamily: "var(--font-body)" }}>
              So funktioniert es
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mt-4" style={{ color: "oklch(0.2 0.02 80)" }}>
              In 3 Schritten zu deinem <em className="not-italic" style={{ color: "oklch(0.52 0.09 128)" }}>AYU</em>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              return (
                <div
                  key={i}
                  className={`p-8 rounded-2xl border bg-white transition-all duration-700 ${
                    stepsRef.visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
                  }`}
                  style={{ transitionDelay: `${i * 150}ms`, borderColor: "oklch(0.9 0.01 80)" }}
                >
                  <span className="text-4xl font-bold" style={{ color: "oklch(0.52 0.09 128 / 0.15)" }}>{s.num}</span>
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mt-4 mb-5" style={{ background: "oklch(0.52 0.09 128 / 0.08)" }}>
                    <Icon className="w-6 h-6" style={{ color: "oklch(0.52 0.09 128)" }} />
                  </div>
                  <h3 className="text-lg font-bold mb-3" style={{ color: "oklch(0.2 0.02 80)" }}>{s.title}</h3>
                  <p className="text-sm leading-relaxed mb-4" style={{ color: "oklch(0.45 0.03 50)", fontFamily: "var(--font-body)" }}>{s.desc}</p>
                  <span className="inline-flex items-center gap-1.5 text-sm font-medium" style={{ color: "oklch(0.52 0.09 128)", fontFamily: "var(--font-body)" }}>
                    <Clock className="w-4 h-4" /> {s.time}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
       *  4. WARUM AYU — Accordion
       * ═══════════════════════════════════════════════════════════ */}
      <section ref={accordionRef.ref} className="py-24 lg:py-32" style={{ background: "oklch(0.97 0.01 80)" }}>
        <div className="container max-w-3xl">
          <div className="text-center mb-16">
            <span className="text-sm font-semibold tracking-[0.2em] uppercase" style={{ color: "oklch(0.52 0.09 128)", fontFamily: "var(--font-body)" }}>
              Warum AYU
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mt-4" style={{ color: "oklch(0.2 0.02 80)" }}>
              Was AYU <em className="not-italic" style={{ color: "oklch(0.52 0.09 128)" }}>anders</em> macht
            </h2>
          </div>

          <div className="space-y-3">
            {ACCORDION_FEATURES.map((item, i) => {
              const Icon = item.icon;
              const isOpen = activeAccordion === i;
              return (
                <div
                  key={i}
                  className="border rounded-2xl overflow-hidden transition-all duration-300"
                  style={{ borderColor: "oklch(0.88 0.02 80)" }}
                >
                  <button
                    onClick={() => setActiveAccordion(isOpen ? -1 : i)}
                    className="w-full flex items-center gap-4 p-5 text-left"
                  >
                    <Icon className="w-5 h-5 shrink-0" style={{ color: "oklch(0.52 0.09 128)" }} />
                    <span className="font-semibold text-base flex-1" style={{ color: "oklch(0.25 0.03 50)", fontFamily: "var(--font-body)" }}>
                      {item.title}
                    </span>
                    <ChevronDown
                      className={`w-5 h-5 shrink-0 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
                      style={{ color: "oklch(0.52 0.09 128)" }}
                    />
                  </button>
                  <div className={`overflow-hidden transition-all duration-300 ${isOpen ? "max-h-96 pb-5 px-5 pl-14" : "max-h-0"}`}>
                    <p className="text-sm leading-relaxed" style={{ color: "oklch(0.45 0.03 50)", fontFamily: "var(--font-body)" }}>
                      {item.desc}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
       *  5. IM VERGLEICH — Comparison Table
       * ═══════════════════════════════════════════════════════════ */}
      <section ref={comparisonRef.ref} className="py-24 lg:py-32" style={{ background: "oklch(0.95 0.008 80)" }}>
        <div className="container max-w-3xl">
          <div className="text-center mb-16">
            <span className="text-sm font-semibold tracking-[0.2em] uppercase" style={{ color: "oklch(0.52 0.09 128)", fontFamily: "var(--font-body)" }}>
              Im Vergleich
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mt-4" style={{ color: "oklch(0.2 0.02 80)" }}>
              AYU vs. <em className="not-italic" style={{ color: "oklch(0.52 0.09 128)" }}>traditionelle Wege</em>
            </h2>
          </div>

          <div className="rounded-2xl overflow-hidden border" style={{ borderColor: "oklch(0.9 0.01 80)" }}>
            {/* Header */}
            <div
              className="grid grid-cols-4 p-4"
              style={{ background: "linear-gradient(135deg, oklch(0.18 0.03 128), oklch(0.22 0.04 100))" }}
            >
              <div />
              <div className="text-center font-bold text-sm" style={{ color: "oklch(0.78 0.11 85)", fontFamily: "var(--font-body)" }}>AYU</div>
              <div className="text-center font-medium text-sm" style={{ color: "oklch(0.7 0.02 80)", fontFamily: "var(--font-body)" }}>Ernährungsberatung</div>
              <div className="text-center font-medium text-sm" style={{ color: "oklch(0.7 0.02 80)", fontFamily: "var(--font-body)" }}>Gesundheits-Apps</div>
            </div>
            {/* Rows */}
            {COMPARISON.map((row, i) => (
              <div
                key={i}
                className="grid grid-cols-4 p-4 items-center border-t"
                style={{ borderColor: "oklch(0.92 0.01 80)", background: i % 2 === 0 ? "white" : "oklch(0.97 0.005 80)" }}
              >
                <span className="text-sm font-medium" style={{ color: "oklch(0.3 0.03 50)", fontFamily: "var(--font-body)" }}>{row.label}</span>
                <div className="flex justify-center">
                  {row.ayu ? <Check className="w-5 h-5" style={{ color: "oklch(0.52 0.09 128)" }} /> : <Minus className="w-5 h-5" style={{ color: "oklch(0.7 0.02 80)" }} />}
                </div>
                <div className="flex justify-center">
                  {row.therapy ? <Check className="w-5 h-5" style={{ color: "oklch(0.52 0.09 128 / 0.5)" }} /> : <Minus className="w-5 h-5" style={{ color: "oklch(0.7 0.02 80)" }} />}
                </div>
                <div className="flex justify-center">
                  {row.apps ? <Check className="w-5 h-5" style={{ color: "oklch(0.52 0.09 128 / 0.5)" }} /> : <Minus className="w-5 h-5" style={{ color: "oklch(0.7 0.02 80)" }} />}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
       *  6. KÖRPER-GEIST-VERBINDUNG
       * ═══════════════════════════════════════════════════════════ */}
      <section ref={mikrobiomRef.ref} className="py-24 lg:py-32" style={{ background: "oklch(0.97 0.01 80)" }}>
        <div className="container">
          <div className="text-center mb-16">
            <span className="text-sm font-semibold tracking-[0.2em] uppercase" style={{ color: "oklch(0.52 0.09 128)", fontFamily: "var(--font-body)" }}>
              Die Körper-Geist-Verbindung
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mt-4" style={{ color: "oklch(0.2 0.02 80)" }}>
              Warum dein <em className="not-italic" style={{ color: "oklch(0.52 0.09 128)" }}>Darm</em> deine Stimmung beeinflusst
            </h2>
            <p className="text-lg mt-4 max-w-2xl mx-auto" style={{ color: "oklch(0.45 0.03 50)", fontFamily: "var(--font-body)" }}>
              95% deines Serotonins wird im Darm produziert. Wenn dort etwas nicht stimmt, spürst du es in deiner Stimmung, deiner Energie und deinem Antrieb.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Left: What the test shows */}
            <div className="p-8 rounded-2xl border bg-white" style={{ borderColor: "oklch(0.9 0.01 80)" }}>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "oklch(0.52 0.09 128 / 0.08)" }}>
                  <Dna className="w-5 h-5" style={{ color: "oklch(0.52 0.09 128)" }} />
                </div>
                <h3 className="text-xl font-bold" style={{ color: "oklch(0.2 0.02 80)" }}>Was der DNA- &amp; Mikrobiom-Test zeigt</h3>
              </div>
              <div className="space-y-5">
                {MIKROBIOM_ITEMS.map((item, i) => {
                  const Icon = item.icon;
                  return (
                    <div key={i} className="flex gap-3">
                      <div className="w-2 h-2 rounded-full mt-2 shrink-0" style={{ background: "oklch(0.78 0.11 85)" }} />
                      <div>
                        <h4 className="font-bold text-sm" style={{ color: "oklch(0.25 0.03 50)" }}>{item.title}</h4>
                        <p className="text-sm mt-0.5" style={{ color: "oklch(0.45 0.03 50)", fontFamily: "var(--font-body)" }}>{item.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Right: The difference */}
            <div className="p-8 rounded-2xl border bg-white" style={{ borderColor: "oklch(0.9 0.01 80)" }}>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "oklch(0.78 0.11 85 / 0.1)" }}>
                  <Sparkles className="w-5 h-5" style={{ color: "oklch(0.78 0.11 85)" }} />
                </div>
                <h3 className="text-xl font-bold" style={{ color: "oklch(0.2 0.02 80)" }}>Der Unterschied für deinen AYU</h3>
              </div>

              <div className="space-y-4">
                <div className="p-5 rounded-xl" style={{ background: "oklch(0.95 0.008 80)" }}>
                  <span className="text-xs font-bold tracking-[0.15em] uppercase" style={{ color: "oklch(0.52 0.09 128)", fontFamily: "var(--font-body)" }}>
                    Ohne DNA-Daten
                  </span>
                  <p className="text-sm mt-2 leading-relaxed" style={{ color: "oklch(0.4 0.03 50)", fontFamily: "var(--font-body)" }}>
                    Dein AYU kennt deinen Lebensstil und kann auf ganzheitlicher Ebene beraten. Er versteht deine Muster, deine Herkunft, deine Erfahrungen.
                  </p>
                </div>
                <div className="p-5 rounded-xl" style={{ background: "oklch(0.52 0.09 128 / 0.05)", border: "1px solid oklch(0.52 0.09 128 / 0.15)" }}>
                  <span className="text-xs font-bold tracking-[0.15em] uppercase" style={{ color: "oklch(0.52 0.09 128)", fontFamily: "var(--font-body)" }}>
                    Mit DNA-Daten
                  </span>
                  <p className="text-sm mt-2 leading-relaxed" style={{ color: "oklch(0.4 0.03 50)", fontFamily: "var(--font-body)" }}>
                    Dein AYU versteht zusätzlich, wie dein Körper deine Stimmung beeinflusst. Wenn du sagst &bdquo;Ich bin so müde und antriebslos&ldquo;, kann er erkennen, ob das mit einem Serotonin- oder Nährstoffmangel zusammenhängen könnte &mdash; und ganzheitlich beraten.
                  </p>
                </div>
              </div>

              <p className="text-center mt-6 italic text-sm" style={{ color: "oklch(0.45 0.03 50)" }}>
                &bdquo;Geist und Körper sind nicht getrennt.&ldquo;
              </p>
              <p className="text-center text-xs mt-1" style={{ color: "oklch(0.55 0.02 50)", fontFamily: "var(--font-body)" }}>
                Die DNA-Analyse macht diese Verbindung sichtbar.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
       *  7. ENTSCHEIDUNGSHILFE
       * ═══════════════════════════════════════════════════════════ */}
      <section ref={decisionRef.ref} className="py-24 lg:py-32" style={{ background: "oklch(0.95 0.008 80)" }}>
        <div className="container max-w-5xl">
          <div className="text-center mb-16">
            <span className="text-sm font-semibold tracking-[0.2em] uppercase" style={{ color: "oklch(0.52 0.09 128)", fontFamily: "var(--font-body)" }}>
              Entscheidungshilfe
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mt-4" style={{ color: "oklch(0.2 0.02 80)" }}>
              Was ist das <em className="not-italic" style={{ color: "oklch(0.52 0.09 128)" }}>Richtige</em> für dich?
            </h2>
            <p className="text-lg mt-4" style={{ color: "oklch(0.45 0.03 50)", fontFamily: "var(--font-body)" }}>
              Es geht nicht um den Preis &mdash; es geht darum, was du gerade brauchst.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Solo */}
            <div>
              <div className="flex items-center gap-2 mb-6">
                <Leaf className="w-5 h-5" style={{ color: "oklch(0.52 0.09 128)" }} />
                <span className="text-sm font-bold tracking-[0.1em] uppercase" style={{ color: "oklch(0.52 0.09 128)", fontFamily: "var(--font-body)" }}>
                  AYU Solo ist für dich, wenn...
                </span>
              </div>
              <div className="space-y-4">
                {[
                  { t: "Du ganzheitliche Klarheit suchst", d: "Du möchtest deinen Körper, Geist und Lebensstil besser verstehen — mit einem Plan, der dich wirklich kennt." },
                  { t: "Du einen Begleiter brauchst", d: "Jemand, der zuhört, reflektiert und die richtigen Empfehlungen gibt — ohne zu urteilen. 24/7 verfügbar." },
                  { t: "Du sofort starten willst", d: "Innerhalb von 24 Stunden ist dein personalisierter AYU-Plan bereit. Kein Warten, kein Termin." },
                ].map((item, i) => (
                  <div key={i} className="p-5 rounded-2xl bg-white border" style={{ borderColor: "oklch(0.9 0.01 80)" }}>
                    <div className="flex items-start gap-3">
                      <Heart className="w-5 h-5 mt-0.5 shrink-0" style={{ color: "oklch(0.52 0.09 128)" }} />
                      <div>
                        <h4 className="font-bold text-sm" style={{ color: "oklch(0.25 0.03 50)" }}>{item.t}</h4>
                        <p className="text-sm mt-1" style={{ color: "oklch(0.45 0.03 50)", fontFamily: "var(--font-body)" }}>{item.d}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* DNA+ */}
            <div>
              <div className="flex items-center gap-2 mb-6">
                <Dna className="w-5 h-5" style={{ color: "oklch(0.78 0.11 85)" }} />
                <span className="text-sm font-bold tracking-[0.1em] uppercase" style={{ color: "oklch(0.78 0.11 85)", fontFamily: "var(--font-body)" }}>
                  AYU DNA+ ist für dich, wenn...
                </span>
              </div>
              <div className="space-y-4">
                {[
                  { t: "Du körperliche Symptome hast", d: "Müdigkeit, Antriebslosigkeit, Verdauungsprobleme — oft hängen diese direkt mit deiner Ernährung und deinem Mikrobiom zusammen." },
                  { t: "Du den Körper-Geist-Zusammenhang verstehen willst", d: "Serotonin- und Dopaminmangel, Nährstoffdefizite — dein Darm beeinflusst dein Wohlbefinden mehr als du denkst." },
                  { t: "Du ganzheitlich an dir arbeiten willst", d: "Dein AYU kennt nicht nur deine Geschichte, sondern auch deinen Körper — für eine tiefere, umfassendere Beratung." },
                ].map((item, i) => (
                  <div key={i} className="p-5 rounded-2xl bg-white border" style={{ borderColor: "oklch(0.9 0.01 80)" }}>
                    <div className="flex items-start gap-3">
                      <Sparkles className="w-5 h-5 mt-0.5 shrink-0" style={{ color: "oklch(0.78 0.11 85)" }} />
                      <div>
                        <h4 className="font-bold text-sm" style={{ color: "oklch(0.25 0.03 50)" }}>{item.t}</h4>
                        <p className="text-sm mt-1" style={{ color: "oklch(0.45 0.03 50)", fontFamily: "var(--font-body)" }}>{item.d}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
       *  8. FAQ
       * ═══════════════════════════════════════════════════════════ */}
      <section ref={faqRef.ref} className="py-24 lg:py-32" style={{ background: "oklch(0.97 0.01 80)" }}>
        <div className="container max-w-3xl">
          <div className="text-center mb-16">
            <span className="text-sm font-semibold tracking-[0.2em] uppercase" style={{ color: "oklch(0.52 0.09 128)", fontFamily: "var(--font-body)" }}>
              Häufige Fragen
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold mt-4" style={{ color: "oklch(0.2 0.02 80)" }}>
              Das wirst du vielleicht gefragt
            </h2>
          </div>
          <Accordion items={FAQ} />
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
       *  9. PRICING — "DEINE INVESTITION"
       * ═══════════════════════════════════════════════════════════ */}
      <section ref={pricingRef.ref} className="py-24 lg:py-32" style={{ background: "oklch(0.95 0.008 80)" }}>
        <div className="container max-w-5xl">
          <div className="text-center mb-16">
            <span className="text-sm font-semibold tracking-[0.2em] uppercase" style={{ color: "oklch(0.52 0.09 128)", fontFamily: "var(--font-body)" }}>
              Deine Investition
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mt-4" style={{ color: "oklch(0.2 0.02 80)" }}>
              Zwei Wege. <em className="not-italic" style={{ color: "oklch(0.52 0.09 128)" }}>Ein Ziel.</em>
            </h2>
            <p className="text-lg mt-4" style={{ color: "oklch(0.45 0.03 50)", fontFamily: "var(--font-body)" }}>
              Einmalig investieren. 1. Jahr App inklusive. Transparent und fair.
            </p>
          </div>

          {/* Pricing Cards */}
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {/* Solo */}
            <div
              className={`p-8 rounded-3xl border-2 bg-white transition-all duration-700 ${
                pricingRef.visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              }`}
              style={{ borderColor: "oklch(0.52 0.09 128)" }}
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "oklch(0.52 0.09 128 / 0.08)" }}>
                  <Leaf className="w-5 h-5" style={{ color: "oklch(0.52 0.09 128)" }} />
                </div>
                <div>
                  <h3 className="text-xl font-bold" style={{ color: "oklch(0.2 0.02 80)" }}>AYU Solo</h3>
                  <p className="text-sm" style={{ color: "oklch(0.45 0.03 50)", fontFamily: "var(--font-body)" }}>Ganzheitlicher Gesundheitsplan</p>
                </div>
              </div>

              <div className="mt-6 mb-2">
                <span className="text-5xl font-bold" style={{ color: "oklch(0.2 0.02 80)" }}>1.490 &euro;</span>
                <span className="text-base ml-2" style={{ color: "oklch(0.5 0.02 50)", fontFamily: "var(--font-body)" }}>einmalig</span>
              </div>
              <p className="text-sm mb-8" style={{ color: "oklch(0.52 0.09 128)", fontFamily: "var(--font-body)" }}>
                Ab ca. 62 &euro;/Monat (24 Raten) oder Inhouse-Raten
              </p>

              <div className="space-y-3 mb-8">
                {[
                  "Personalisierter AYU basierend auf umfassender Anamnese",
                  "Herkunft, Lebensstil & Ernährung erfasst",
                  "Mentales Befinden & familiäre Muster berücksichtigt",
                  "Innerhalb von 24 Stunden einsatzbereit",
                  "Unbegrenzt nutzbar",
                  "Zugang zur AYU-Webapp (Kompass, Tagebuch, Chat)",
                  "1. Jahr App-Nutzung inklusive, danach nur 5 €/Monat oder 40 €/Jahr",
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-2.5">
                    <Check className="w-5 h-5 shrink-0 mt-0.5" style={{ color: "oklch(0.52 0.09 128)" }} />
                    <span className="text-sm" style={{ color: "oklch(0.35 0.03 50)", fontFamily: "var(--font-body)" }}>{item}</span>
                  </div>
                ))}
              </div>

              <button
                className="w-full py-3.5 rounded-full font-semibold text-sm transition-all duration-300"
                style={{
                  background: "oklch(0.52 0.09 128)",
                  color: "oklch(0.97 0.01 80)",
                  fontFamily: "var(--font-body)",
                }}
              >
                Ausgewählt
              </button>
            </div>

            {/* DNA+ */}
            <div
              className={`p-8 rounded-3xl border-2 bg-white relative transition-all duration-700 delay-150 ${
                pricingRef.visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              }`}
              style={{ borderColor: "oklch(0.78 0.11 85)" }}
            >
              {/* Badge */}
              <div
                className="absolute -top-3 right-6 px-4 py-1 rounded-full text-xs font-bold"
                style={{ background: "oklch(0.78 0.11 85)", color: "oklch(0.18 0.02 280)", fontFamily: "var(--font-body)" }}
              >
                Empfohlen
              </div>

              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "oklch(0.78 0.11 85 / 0.1)" }}>
                  <Dna className="w-5 h-5" style={{ color: "oklch(0.78 0.11 85)" }} />
                </div>
                <div>
                  <h3 className="text-xl font-bold" style={{ color: "oklch(0.2 0.02 80)" }}>AYU DNA+</h3>
                  <p className="text-sm" style={{ color: "oklch(0.45 0.03 50)", fontFamily: "var(--font-body)" }}>Ganzheitlich &amp; körperlich verstehen</p>
                </div>
              </div>

              <div className="mt-6 mb-2">
                <span className="text-5xl font-bold" style={{ color: "oklch(0.2 0.02 80)" }}>1.990 &euro;</span>
                <span className="text-base ml-2" style={{ color: "oklch(0.5 0.02 50)", fontFamily: "var(--font-body)" }}>einmalig</span>
              </div>
              <p className="text-sm mb-8" style={{ color: "oklch(0.78 0.11 85)", fontFamily: "var(--font-body)" }}>
                Ab ca. 83 &euro;/Monat (24 Raten) oder Inhouse-Raten
              </p>

              <div className="space-y-3 mb-4">
                <p className="text-sm font-semibold" style={{ color: "oklch(0.35 0.03 50)", fontFamily: "var(--font-body)" }}>
                  Alles aus AYU Solo &mdash; plus:
                </p>
                {[
                  "DNA-Analyse &amp; Darm-Mikrobiom-Test (Testkit wird zugeschickt)",
                  "Analyse von Serotonin- &amp; Dopamin-Zusammenhängen",
                  "Nährstoffmängel erkennen &amp; verstehen",
                  "Körper-Geist-Verbindung in deinem AYU integriert",
                  "Ernährungsempfehlungen basierend auf deinem Mikrobiom",
                  "1. Jahr App-Nutzung inklusive, danach nur 5 €/Monat oder 40 €/Jahr",
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-2.5">
                    <Check className="w-5 h-5 shrink-0 mt-0.5" style={{ color: "oklch(0.52 0.09 128)" }} />
                    <span className="text-sm" style={{ color: "oklch(0.35 0.03 50)", fontFamily: "var(--font-body)" }} dangerouslySetInnerHTML={{ __html: item }} />
                  </div>
                ))}
              </div>

              {/* Zusätzlich enthalten */}
              <div className="p-4 rounded-xl mt-6 mb-6" style={{ background: "oklch(0.95 0.008 80)" }}>
                <p className="text-xs font-bold tracking-[0.1em] uppercase mb-3" style={{ color: "oklch(0.5 0.03 50)", fontFamily: "var(--font-body)" }}>
                  Zusätzlich enthalten
                </p>
                <div className="space-y-2">
                  {[
                    "Dein AYU kennt deine körperlichen Daten und kann ganzheitlich beraten",
                    "Zusammenhang zwischen Darmgesundheit und Wohlbefinden sichtbar",
                    "Wissenschaftlich fundierte Ernährungshinweise",
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <Sparkles className="w-4 h-4 shrink-0 mt-0.5" style={{ color: "oklch(0.78 0.11 85)" }} />
                      <span className="text-xs" style={{ color: "oklch(0.4 0.03 50)", fontFamily: "var(--font-body)" }}>{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              <button
                className="w-full py-3.5 rounded-full font-semibold text-sm transition-all duration-300 border-2"
                style={{
                  borderColor: "oklch(0.78 0.11 85)",
                  color: "oklch(0.78 0.11 85)",
                  background: "transparent",
                  fontFamily: "var(--font-body)",
                }}
              >
                Auswählen
              </button>
            </div>
          </div>

          {/* Payment options */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="p-6 rounded-2xl bg-white border" style={{ borderColor: "oklch(0.9 0.01 80)" }}>
              <div className="flex items-center gap-2 mb-3">
                <Shield className="w-5 h-5" style={{ color: "oklch(0.52 0.09 128)" }} />
                <h4 className="font-bold text-sm" style={{ color: "oklch(0.25 0.03 50)" }}>Externe Ratenzahlung</h4>
              </div>
              <p className="text-sm mb-4" style={{ color: "oklch(0.45 0.03 50)", fontFamily: "var(--font-body)" }}>
                Bis zu 24 Monatsraten &mdash; je nach Bonität und Region über:
              </p>
              <div className="flex gap-2 mb-4">
                {["PayPal", "Klarna", "Amazon Pay"].map((p) => (
                  <span key={p} className="px-3 py-1.5 rounded-lg text-xs font-medium border" style={{ borderColor: "oklch(0.88 0.02 80)", color: "oklch(0.35 0.03 50)", fontFamily: "var(--font-body)" }}>
                    {p}
                  </span>
                ))}
              </div>
              <p className="text-xs" style={{ color: "oklch(0.52 0.09 128)", fontFamily: "var(--font-body)" }}>
                <strong>Solo:</strong> ab ca. 62 &euro;/Monat (24 Raten)<br />
                <strong>DNA+:</strong> ab ca. 83 &euro;/Monat (24 Raten)
              </p>
            </div>

            <div className="p-6 rounded-2xl bg-white border relative" style={{ borderColor: "oklch(0.9 0.01 80)" }}>
              <div className="absolute -top-2.5 right-4 px-3 py-0.5 rounded-full text-xs font-bold" style={{ background: "oklch(0.52 0.09 128)", color: "white", fontFamily: "var(--font-body)" }}>
                Beliebt
              </div>
              <div className="flex items-center gap-2 mb-3">
                <Heart className="w-5 h-5" style={{ color: "oklch(0.52 0.09 128)" }} />
                <h4 className="font-bold text-sm" style={{ color: "oklch(0.25 0.03 50)" }}>Inhouse-Ratenzahlung</h4>
              </div>
              <p className="text-sm mb-4" style={{ color: "oklch(0.45 0.03 50)", fontFamily: "var(--font-body)" }}>
                Direkt mit uns &mdash; unkompliziert und ohne Bonitätsprüfung:
              </p>
              <div className="space-y-2 mb-3">
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5" style={{ color: "oklch(0.52 0.09 128)" }} />
                  <span className="text-sm font-semibold" style={{ color: "oklch(0.25 0.03 50)", fontFamily: "var(--font-body)" }}>250 &euro; Anzahlung</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5" style={{ color: "oklch(0.52 0.09 128)" }} />
                  <span className="text-sm font-semibold" style={{ color: "oklch(0.25 0.03 50)", fontFamily: "var(--font-body)" }}>Danach nur 100 &euro;/Monat</span>
                </div>
              </div>
              <p className="text-sm italic" style={{ color: "oklch(0.52 0.09 128)", fontFamily: "var(--font-body)" }}>
                Weniger als eine Beratungssitzung pro Monat.
              </p>
            </div>
          </div>

          {/* App note */}
          <div className="p-5 rounded-2xl text-center" style={{ background: "oklch(0.52 0.09 128 / 0.06)", border: "1px solid oklch(0.52 0.09 128 / 0.15)" }}>
            <p className="text-sm" style={{ color: "oklch(0.35 0.03 50)", fontFamily: "var(--font-body)" }}>
              <strong>App-Nutzung:</strong> Das erste Jahr ist bei beiden Paketen inklusive. Danach nur{" "}
              <strong style={{ color: "oklch(0.52 0.09 128)" }}>5 &euro;/Monat</strong> oder{" "}
              <strong style={{ color: "oklch(0.52 0.09 128)" }}>40 &euro;/Jahr</strong> &mdash; damit dein AYU immer für dich da bleibt.
            </p>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
       *  10. FINAL CTA
       * ═══════════════════════════════════════════════════════════ */}
      <section
        ref={ctaRef.ref}
        className="py-24 lg:py-32 relative overflow-hidden"
        style={{ background: "linear-gradient(135deg, oklch(0.18 0.03 128), oklch(0.22 0.04 100))" }}
      >
        <div className="absolute inset-0 mandala-overlay" />
        <div className="container relative z-10 text-center">
          <Sparkles className="w-10 h-10 mx-auto mb-6" style={{ color: "oklch(0.78 0.11 85)" }} />
          <h2
            className={`text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight transition-all duration-1000 ${
              ctaRef.visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
            style={{ color: "oklch(0.97 0.01 80)" }}
          >
            Du verdienst jemanden,<br />
            <em className="not-italic" style={{ color: "oklch(0.78 0.11 85)" }}>der dich versteht.</em>
          </h2>
          <p className="text-lg mt-4 mb-10" style={{ color: "oklch(0.8 0.02 80)", fontFamily: "var(--font-body)" }}>
            Dein AYU wartet auf dich. Personalisiert. Geduldig. Immer da.
          </p>
          <Link
            href="/termin"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold text-base transition-all duration-300 hover:scale-105"
            style={{
              background: "oklch(0.52 0.09 128)",
              color: "oklch(0.97 0.01 80)",
              boxShadow: "0 0 30px oklch(0.52 0.09 128 / 0.3)",
              fontFamily: "var(--font-body)",
            }}
          >
            <Sparkles className="w-5 h-5" />
            Jetzt starten
            <ArrowRight className="w-5 h-5" />
          </Link>
          <p className="text-sm mt-6" style={{ color: "oklch(0.6 0.02 80)", fontFamily: "var(--font-body)" }}>
            Einmalige Investition &middot; 1. Jahr App inklusive &middot; Flexible Ratenzahlung
          </p>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
       *  11. FOOTER
       * ═══════════════════════════════════════════════════════════ */}
      <footer className="py-8" style={{ background: "oklch(0.15 0.02 128)" }}>
        <div className="container flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src={IMAGES.logo} alt="AYU" className="h-8 opacity-60" />
            <span className="text-sm" style={{ color: "oklch(0.5 0.02 80)", fontFamily: "var(--font-body)" }}>
              &copy; 2026 AYU Health
            </span>
          </div>
          <div className="flex gap-6">
            {[
              { label: "Impressum", href: "/impressum" },
              { label: "Datenschutz", href: "/datenschutz" },
            ].map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className="text-sm transition-colors duration-300 hover:opacity-80"
                style={{ color: "oklch(0.5 0.02 80)", fontFamily: "var(--font-body)" }}
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
}
